import { Link } from "react-router-dom";
import { Seo } from "../components/seo/Seo";
import { Breadcrumbs } from "../components/seo/Breadcrumbs";

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Course",
  "@id": "https://makershub.be/workshops/line-follower-robot#course",
  name: "Line-follower racerobot — 3-sessie Arduino-reeks voor kinderen",
  description:
    "Drie zaterdagsessies waarin je kind een autonome line-following racerobot bouwt. Arduino, motoren, sensoren — eindigt met race tegen klasgenoten.",
  provider: {
    "@type": "EducationalOrganization",
    name: "Makers Hub",
    url: "https://makershub.be/",
  },
  educationalLevel: "Basisschool 5de-6de leerjaar",
  audience: {
    "@type": "EducationalAudience",
    educationalRole: "student",
    audienceType: "Kinderen 10-12 jaar",
  },
  teaches: [
    "Arduino programmeren (PWM, sensors, control loops)",
    "Motoraansturing (H-bridge)",
    "Lijnsensoren uitlezen",
    "Algoritmiek — line-following logica",
    "Iteratief verbeteren (testen, tweaken, racen)",
  ],
  courseMode: "in-person",
  inLanguage: "nl-BE",
  offers: {
    "@type": "Offer",
    price: "120",
    priceCurrency: "EUR",
    availability: "https://schema.org/PreOrder",
    url: "https://makershub.be/workshops/line-follower-robot",
    category: "Workshop reeks kinderen",
  },
  hasCourseInstance: {
    "@type": "CourseInstance",
    courseMode: "in-person",
    courseWorkload: "PT9H",
    location: {
      "@type": "Place",
      name: "Klaslokaal in Vlaanderen — locatie meegedeeld bij inschrijving",
      address: {
        "@type": "PostalAddress",
        addressCountry: "BE",
        addressRegion: "Vlaanderen",
      },
    },
    instructor: {
      "@type": "Person",
      name: "Tuur Vanholen",
      url: "https://makershub.be/over",
    },
    maximumAttendeeCapacity: 8,
  },
};

export function WorkshopLineFollowerPage() {
  return (
    <>
      <Seo
        title="Line-follower racerobot — 3-sessie reeks kinderen 10-12 | Makers Hub"
        description="In 3 zaterdagen bouwt je kind een autonome racerobot die zelf een lijn volgt. Arduino, motoren, sensoren. €120 voor de hele reeks. Eindigt met race-dag."
        path="/workshops/line-follower-robot"
        schema={SCHEMA}
      />
      <article className="page page-workshop">
        <div className="container">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Workshops", to: "/workshops" },
              { label: "Line-follower racerobot" },
            ]}
          />

          <header className="page-hero">
            <span className="section-tag tag-orange">Reeks · 3 zaterdagen · 9u</span>
            <h1>
              Line-follower <span className="accent">racerobot</span>
            </h1>
            <p className="lead">
              Drie zaterdagen werken aan één doel: een autonome racerobot die op eigen kracht een
              lijn volgt. Bouwen, programmeren, racen. Voor kinderen 10-12 die meer willen dan een
              eenmalige workshop.
            </p>
            <div className="page-meta">
              <span><strong>€120</strong> de reeks</span>
              <span><strong>9u</strong> totaal · 3× 3u</span>
              <span><strong>Max 8</strong> kinderen</span>
              <span><strong>10-12 jaar</strong></span>
              <span className="badge-pre">Binnenkort beschikbaar</span>
            </div>
            <div className="page-ctas">
              <a href="/#werkplaats" className="btn btn-primary btn-lg">
                Op de wachtlijst →
              </a>
              <Link to="/workshops/simon-says" className="btn btn-ghost btn-lg">
                Liever 1-dag workshop?
              </Link>
            </div>
          </header>

          <div className="page-hero-img">
            <img src="/assets/projects/project-line-follower.png" alt="Line-follower racerobot" />
          </div>

          <section className="page-section">
            <h2>Wat bouwt je kind</h2>
            <p>
              Een autonome robot ter grootte van een handpalm. Twee wielen, twee sensoren onderaan
              die het verschil tussen zwart en wit op een vloermat aflezen. Een Arduino die op
              basis van die sensors beslist naar links of rechts te draaien om de lijn te blijven
              volgen.
            </p>
            <p>
              Eindresultaat: een robot die je op een parcours zet en die zonder hulp de lijn
              volgt — soms snel, soms haperend, altijd zijn eigen.
            </p>
          </section>

          <section className="page-section">
            <h2>Wat leert je kind</h2>
            <ul className="check-list">
              <li>
                <strong>Een complex project opbouwen.</strong> Niet alles werkt op dag 1.
                Stap-per-stap iets bouwen over drie weken — projectmatig denken.
              </li>
              <li>
                <strong>Sensoren begrijpen.</strong> Hoe weet een robot waar hij is? Lijnsensoren
                geven analoge waardes — we leren die uitlezen en interpreteren.
              </li>
              <li>
                <strong>Motoraansturing.</strong> H-bridge driver, PWM voor snelheid, links- en
                rechtsom. Echte robotica-bouwstenen.
              </li>
              <li>
                <strong>Algorithmen.</strong> Hoe beslis je naar links of rechts? Eenvoudige
                logica eerst, dan PID-regeling als uitdaging.
              </li>
              <li>
                <strong>Iteratief verbeteren.</strong> Werkt de robot? Sneller maken. Beter rond
                bochten? Probeer en pas aan.
              </li>
            </ul>
          </section>

          <section className="page-section">
            <h2>Programma — week per week</h2>
            <ol className="step-list">
              <li>
                <strong>Sessie 1 — Bouwen</strong>
                <p>
                  Chassis, wielen, motoren, Arduino, batterijhouder. Alle componenten op het
                  frame. Eerst de motoren laten draaien zonder programmering. Aan het einde van
                  de dag: robot rijdt rechtuit (op commando van een drukknop).
                </p>
              </li>
              <li>
                <strong>Sessie 2 — Sensoren + logica</strong>
                <p>
                  Lijnsensoren toevoegen. Sensorwaarden afdrukken om te zien wat ze meten.
                  Eerste line-following code: blijf op de lijn met simpele if-then logica. Robot
                  volgt rechte lijn aan einde sessie.
                </p>
              </li>
              <li>
                <strong>Sessie 3 — Tweaken + racen</strong>
                <p>
                  Code verfijnen voor bochten. Snelheid optimaliseren. Daarna: race-circuit
                  uitleggen, kinderen krijgen tijd om hun robot te tunen, en we sluiten af met
                  tijdritten. Wie heeft de snelste robot?
                </p>
              </li>
            </ol>
          </section>

          <section className="page-section">
            <h2>Voor wie</h2>
            <p>
              Kinderen <strong>10-12 jaar</strong> (5de-6de leerjaar) die graag iets bouwen dat
              meer is dan een eenmalig project. Geen voorkennis nodig, maar als je kind net de
              Simon Says heeft gedaan = perfecte vervolgstap.
            </p>
            <p>
              <strong>Vereist:</strong> eigen laptop, kunnen aanwezig zijn op alle drie de
              zaterdagen. Mis je er één? Mail ons, we vinden een oplossing.
            </p>
          </section>

          <section className="page-section">
            <h2>Praktisch</h2>
            <table className="info-table">
              <tbody>
                <tr><th>Prijs</th><td>€120 voor de volledige reeks (€40/sessie · alle componenten + robot inbegrepen)</td></tr>
                <tr><th>Duur</th><td>3 zaterdagen × 3 uur = 9 uur</td></tr>
                <tr><th>Groepsgrootte</th><td>Max 8 kinderen</td></tr>
                <tr><th>Leeftijd</th><td>10-12 jaar (5de-6de leerjaar)</td></tr>
                <tr><th>Voorkennis</th><td>Geen — Simon Says workshop is een mooie opwarmer</td></tr>
                <tr><th>Mee te brengen</th><td>Eigen laptop (Arduino IDE installeren we samen)</td></tr>
                <tr><th>Status</th><td>Binnenkort beschikbaar — laat je e-mail achter voor data</td></tr>
              </tbody>
            </table>
          </section>

          <section className="page-section page-section-cta">
            <h2>Op de wachtlijst</h2>
            <p>
              Reeks start zodra we voldoende inschrijvingen hebben. Laat je e-mail achter — je
              hoort als eerste wanneer we data prikken.
            </p>
            <div className="page-ctas">
              <a href="/#werkplaats" className="btn btn-primary btn-lg">
                Wachtlijst →
              </a>
              <Link to="/workshops/simon-says" className="btn btn-ghost btn-lg">
                Bekijk Simon Says (al beschikbaar)
              </Link>
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
